import { useState, useRef } from "react";
import { Upload, X, Car, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { generateAndUploadVehicleImage } from "@/lib/generateVehicleImage";
import { useQueryClient } from "@tanstack/react-query";

interface VehicleImageUploadProps {
  imageUrl?: string | null;
  onImageChange: (url: string | null) => void;
  disabled?: boolean;
  /** Vehicle info needed for AI generation. Pass when editing an existing vehicle. */
  vehicleInfo?: {
    vehicleId: string;
    make: string;
    model: string;
    year?: number;
    nickname?: string;
    color?: string;
  } | null;
}

export const VehicleImageUpload = ({
  imageUrl,
  onImageChange,
  disabled = false,
  vehicleInfo = null,
}: VehicleImageUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setIsUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("vehicle-images")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("vehicle-images")
        .getPublicUrl(fileName);

      onImageChange(publicUrl);
      toast.success("Image uploaded successfully");
    } catch (error: any) {
      toast.error("Failed to upload image: " + error.message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveImage = () => {
    onImageChange(null);
  };

  const handleGenerateAI = async () => {
    if (!vehicleInfo) return;

    const hasMakeModel =
      vehicleInfo.make && vehicleInfo.make !== "Unknown" &&
      vehicleInfo.model && vehicleInfo.model !== "Unknown";

    if (!hasMakeModel && !vehicleInfo.nickname) {
      toast.error("Add a make & model or nickname first to generate an AI photo");
      return;
    }

    setIsGenerating(true);
    try {
      await generateAndUploadVehicleImage(vehicleInfo);

      // Fetch the updated vehicle to get the new image_url
      const { data } = await supabase
        .from("vehicles")
        .select("image_url")
        .eq("id", vehicleInfo.vehicleId)
        .single();

      if (data?.image_url) {
        onImageChange(data.image_url);
        // Invalidate the vehicles query so the list view updates immediately
        queryClient.invalidateQueries({ queryKey: ["vehicles"] });
        toast.success("AI photo generated successfully");
      } else {
        toast.error("Image generation did not produce a result");
      }
    } catch {
      toast.error("Failed to generate AI photo");
    } finally {
      setIsGenerating(false);
    }
  };

  const canGenerate = vehicleInfo && !disabled && !isUploading && !isGenerating;

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Vehicle Photo</label>
      <div className="flex items-center gap-4">
        <div className="relative w-24 h-24 rounded-lg border border-input bg-muted flex items-center justify-center overflow-hidden">
          {isGenerating ? (
            <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
          ) : imageUrl ? (
            <>
              <img
                src={imageUrl}
                alt="Vehicle"
                className="w-full h-full object-cover"
              />
              {!disabled && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="absolute top-1 right-1 p-1 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </>
          ) : (
            <Car className="h-8 w-8 text-muted-foreground" />
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled || isUploading || isGenerating}
          />
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isUploading || isGenerating}
            >
              <Upload className="h-4 w-4 mr-2" />
              {isUploading ? "Uploading..." : imageUrl ? "Change Photo" : "Upload Photo"}
            </Button>
            <p className="text-xs text-muted-foreground mt-1">
              Max 5MB, JPG or PNG
            </p>
          </div>
          {canGenerate && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGenerateAI}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {imageUrl ? "Regenerate with AI" : "Generate with AI"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
